import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  CenterFocusStrongOutlined,
  DeleteOutline,
  EditOutlined,
  LinkOutlined,
  ChairOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { GiTheater } from 'react-icons/gi';
import { MdOutlineScreenShare } from 'react-icons/md';
import { TbBroadcast } from 'react-icons/tb';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { PlayFormFields } from '@/components/events/detail/tabs/flows/grid/actions/play.form.fields';
import { RepairPlayerModal } from '@/components/events/detail/tabs/flows/grid/actions/repair-player-modal';
import { LiveKitPreviewModal } from '@/components/preview/livekit-preview-modal';
import { useDeletePlayerMutation } from '@/state/mutations/players/use-delete-player';
import { useIdentifyPlayerMutation } from '@/state/mutations/players/use-identify-player';
import { useUpdatePlayerNameMutation } from '@/state/mutations/players/use-update-player-name';
import { useNotifications } from '@/hooks/use-notifications';
import type { PlayerDto, PlayerType } from '@/types/dtos/player/response/player.dto';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';
import { isPublishingPlayer } from '@/types/dtos/player/response/player.dto';
import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto';
import { getOnAirLivekitRoomIdFromFlow } from '@/helpers/entity-ref.helper';
import { getPlayerTypeCap } from '@/helpers/player-type-caps';
import { ConfigurationInfoTitle } from './configuration-info-title';
import { PlayerDeviceSettings } from './player-device-settings';

type ConfigurationPlayerSectionProps = {
  title: string;
  intro: string;
  /** Optional guidance shown in an info tooltip next to the title (hover + tap). */
  titleInfo?: string;
  players: PlayerDto[];
  /** Real player type for pairing; omit for stub sections. */
  playerType?: PlayerType;
  /** Cap override for stub sections (when playerType is omitted). */
  stubCap?: number;
  stubMode?: boolean;
  /** Stage: always show pairing when empty (no +). */
  alwaysShowPairFormWhenEmpty?: boolean;
  scrollable?: boolean;
  entityId: string;
  entityType: PlayerParentEntity;
  eventSlotId?: string | null;
  liveContext?: LiveContextDto | null;
  disabled?: boolean;
  /** Current room video source. Camera controls show on that player. */
  videoPlayerId?: string | null;
  /** Current room audio source. Microphone controls show on that player. */
  audioPlayerId?: string | null;
  /** Audience camera selector shows on this player. */
  audienceCameraPlayerId?: string | null;
};

function getPlayerIcon(player: PlayerDto) {
  const iconColor = player.isConnected ? 'error' : 'inherit';
  switch (player.type) {
    case 'stagePlayer':
      return <SvgIcon component={GiTheater} inheritViewBox color={iconColor} fontSize="small" />;
    case 'screenPublisher':
      return <SvgIcon component={MdOutlineScreenShare} inheritViewBox color={iconColor} fontSize="small" />;
    case 'roomPlayer':
      return <ChairOutlined color={iconColor} fontSize="small" />;
    case 'onlinePlayer':
      return (
        <SvgIcon
          component={TbBroadcast}
          inheritViewBox
          color={iconColor}
          fontSize="small"
          sx={{ fill: 'none', stroke: 'currentColor' }}
        />
      );
    default:
      return <VisibilityOutlined color={iconColor} fontSize="small" />;
  }
}

function PlayerLabelRow({
  player,
  onRepair,
  onUnpair,
  onPreview,
  canPreview,
  isDeleting,
  actionsDisabled = false,
}: {
  player: PlayerDto;
  onRepair: () => void;
  onUnpair: () => void;
  onPreview?: () => void;
  canPreview: boolean;
  isDeleting: boolean;
  actionsDisabled?: boolean;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const { mutateAsync: updateName, isPending: isRenaming } = useUpdatePlayerNameMutation();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(player.name ?? '');

  const displayName = player.name || `Player ${player.id.slice(-4)}`;

  const commitRename = async () => {
    if (actionsDisabled) {
      setDraftName(player.name ?? '');
      setEditing(false);
      return;
    }
    const next = draftName.trim();
    if (!next || next === (player.name ?? '').trim()) {
      setDraftName(player.name ?? '');
      setEditing(false);
      return;
    }
    try {
      await updateName({ playerId: player.id, name: next });
      notification.notifySuccess(t('flows.detail.configuration.renameSuccess' as TranslationKey));
      setEditing(false);
    } catch {
      notification.notifyError(t('flows.detail.configuration.renameError' as TranslationKey));
      setDraftName(player.name ?? '');
    }
  };

  const statusLabel = player.isConnected
    ? t('common.connected' as TranslationKey)
    : t('common.disconnected' as TranslationKey);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        bgcolor: 'action.hover',
        cursor: canPreview && !editing ? 'pointer' : 'default',
      }}
      onClick={canPreview && !editing ? onPreview : undefined}
    >
      {getPlayerIcon(player)}
      {editing ? (
        <TextField
          size="small"
          value={draftName}
          autoFocus
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={() => void commitRename()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commitRename();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setDraftName(player.name ?? '');
              setEditing(false);
            }
          }}
          disabled={isRenaming}
          sx={{ flex: 1, minWidth: 0 }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
      ) : (
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {displayName}
          </Typography>
          <Tooltip title={t('common.edit' as TranslationKey)}>
            <span>
              <IconButton
                size="small"
                disabled={actionsDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (actionsDisabled) return;
                  setDraftName(player.name ?? '');
                  setEditing(true);
                }}
                aria-label={t('common.edit' as TranslationKey)}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}
      <Typography
        variant="caption"
        color={player.isConnected ? 'error.main' : 'text.secondary'}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {statusLabel}
      </Typography>
      <Stack direction="row" spacing={0.25} onClick={(e) => e.stopPropagation()}>
        <IdentifyPlayerButton player={player} />
        <Tooltip title={t('player.repairTitle' as TranslationKey)}>
          <span>
            <IconButton size="small" onClick={onRepair} disabled={actionsDisabled} color="primary">
              <LinkOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('flows.detail.configuration.unpair' as TranslationKey)}>
          <span>
            <IconButton size="small" onClick={onUnpair} disabled={actionsDisabled || isDeleting} color="error">
              {isDeleting ? <CircularProgress size={18} color="error" /> : <DeleteOutline fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function IdentifyPlayerButton({ player }: { player: PlayerDto }) {
  const t = useTranslations();
  const identifyingRef = useRef(false);
  const seqRef = useRef(0);
  const { mutate } = useIdentifyPlayerMutation();

  const send = (show: boolean) => {
    seqRef.current += 1;
    mutate({ playerId: player.id, show, seq: seqRef.current });
  };

  useEffect(() => {
    return () => {
      if (identifyingRef.current) {
        identifyingRef.current = false;
        seqRef.current += 1;
        mutate({ playerId: player.id, show: false, seq: seqRef.current });
      }
    };
  }, [mutate, player.id]);

  const start = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!player.isConnected) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    identifyingRef.current = true;
    send(true);
  };

  const stop = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!identifyingRef.current) return;
    identifyingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    send(false);
  };

  const isHoldKey = (key: string) => key === 'Enter' || key === ' ';

  const startFromKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!player.isConnected || !isHoldKey(event.key) || event.repeat) return;
    event.preventDefault();
    event.stopPropagation();
    identifyingRef.current = true;
    send(true);
  };

  const stopFromKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!isHoldKey(event.key) || !identifyingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    identifyingRef.current = false;
    send(false);
  };

  const stopOnBlur = () => {
    if (!identifyingRef.current) return;
    identifyingRef.current = false;
    send(false);
  };

  const tooltip = player.isConnected
    ? t('player.identifyTitle' as TranslationKey)
    : t('player.identifyDisconnected' as TranslationKey);

  return (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          size="small"
          color="primary"
          disabled={!player.isConnected}
          onPointerDown={start}
          onPointerUp={stop}
          onPointerCancel={stop}
          onKeyDown={startFromKey}
          onKeyUp={stopFromKey}
          onBlur={stopOnBlur}
          onContextMenu={(event) => event.preventDefault()}
          aria-label={tooltip}
          sx={{ touchAction: 'none' }}
        >
          <CenterFocusStrongOutlined fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function ConfigurationPlayerSection({
  title,
  intro,
  titleInfo,
  players,
  playerType,
  stubCap,
  stubMode = false,
  alwaysShowPairFormWhenEmpty = false,
  scrollable = false,
  entityId,
  entityType,
  eventSlotId,
  liveContext,
  disabled = false,
  videoPlayerId,
  audioPlayerId,
  audienceCameraPlayerId,
}: ConfigurationPlayerSectionProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const { mutateAsync: deletePlayer } = useDeletePlayerMutation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [repairPlayer, setRepairPlayer] = useState<PlayerDto | null>(null);
  const [previewPlayer, setPreviewPlayer] = useState<PlayerDto | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const cap = playerType ? getPlayerTypeCap(playerType) : stubCap;
  const atCap = cap !== undefined && players.length >= cap;
  const isEmpty = players.length === 0;
  const showPairFormAlways = alwaysShowPairFormWhenEmpty && isEmpty && !stubMode;
  const showPairForm = !disabled && (showPairFormAlways || showAddForm);

  const livekitRoomId = getOnAirLivekitRoomIdFromFlow(entityId, liveContext);

  const canPreviewPlayer = (player: PlayerDto) =>
    Boolean(livekitRoomId) &&
    Boolean(liveContext?.isRunning) &&
    isPublishingPlayer(player) &&
    Boolean(player.isConnected || player.isLivekitConnected);

  const addDisabled = disabled || atCap;
  const addTooltip =
    atCap && cap !== undefined
      ? t('flows.detail.configuration.capReached' as TranslationKey, { count: cap })
      : stubMode
        ? t('flows.detail.configuration.comingSoon' as TranslationKey)
        : t('flows.detail.configuration.addPlayer' as TranslationKey);

  const handleUnpair = async (player: PlayerDto) => {
    if (disabled) return;
    const displayName = player.name || `Player ${player.id.slice(-4)}`;
    setDeletingIds((prev) => new Set(prev).add(player.id));
    try {
      await deletePlayer(player.id);
      notification.notifySuccess(t('player.deleteSuccess', { name: displayName }) as TranslationKey);
    } catch {
      notification.notifyError(t('player.deleteError', { name: displayName }) as TranslationKey);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(player.id);
        return next;
      });
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          {titleInfo ? (
            <ConfigurationInfoTitle title={title} info={titleInfo} />
          ) : (
            <Typography variant="subtitle2">{title}</Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {intro}
          </Typography>
        </Stack>
        {alwaysShowPairFormWhenEmpty && !stubMode ? null : (
          <Tooltip title={addTooltip}>
            <span>
              <IconButton
                size="small"
                color={showAddForm ? 'primary' : 'default'}
                disabled={addDisabled && !showAddForm}
                onClick={() => {
                  if (atCap && !showAddForm) return;
                  setShowAddForm((v) => !v);
                }}
                aria-label={t('flows.detail.configuration.addPlayer' as TranslationKey)}
                sx={{ mt: -0.5 }}
              >
                <AddOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Collapse in={showPairForm || (stubMode && showAddForm)} unmountOnExit>
        <Paper elevation={0} sx={{ p: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
          {entityId ? (
            <PlayFormFields
              key={`${playerType ?? 'stub'}-pair-form`}
              entityId={entityId}
              entityType={entityType}
              eventSlotId={eventSlotId ?? undefined}
              fixedType={playerType}
              autoName={!stubMode}
              existingPlayerNames={players.map((p) => p.name)}
              stubMode={stubMode}
              stubMessage={stubMode ? t('flows.detail.configuration.comingSoon' as TranslationKey) : undefined}
              showCancel={!showPairFormAlways}
              onSuccess={() => setShowAddForm(false)}
              onCancel={() => setShowAddForm(false)}
            />
          ) : null}
        </Paper>
      </Collapse>

      {players.length > 0 ? (
        <Box sx={scrollable ? { maxHeight: 280, overflowY: 'auto', pr: 0.5 } : undefined}>
          <Stack spacing={0.75}>
            {players.map((player) => (
              <Stack key={`${player.id}:${player.name ?? ''}`} spacing={0.5}>
                <PlayerLabelRow
                  player={player}
                  canPreview={canPreviewPlayer(player)}
                  isDeleting={deletingIds.has(player.id)}
                  actionsDisabled={disabled}
                  onRepair={() => {
                    if (disabled) return;
                    setRepairPlayer(player);
                  }}
                  onUnpair={() => void handleUnpair(player)}
                  onPreview={() => setPreviewPlayer(player)}
                />
                {stubMode ? null : (
                  <PlayerDeviceSettings
                    player={player}
                    videoPlayerId={videoPlayerId}
                    audioPlayerId={audioPlayerId}
                    audienceCameraPlayerId={audienceCameraPlayerId}
                  />
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : null}

      {repairPlayer ? (
        <RepairPlayerModal
          open={!!repairPlayer}
          onClose={() => setRepairPlayer(null)}
          onSuccess={() => setRepairPlayer(null)}
          player={repairPlayer}
          entityId={entityId}
          entityType={entityType}
        />
      ) : null}

      {previewPlayer && livekitRoomId ? (
        <LiveKitPreviewModal
          open={!!previewPlayer}
          onClose={() => setPreviewPlayer(null)}
          livekitRoomId={livekitRoomId}
          title={previewPlayer.name || `Player ${previewPlayer.id.slice(-4)}`}
          publisherPlayerId={previewPlayer.id}
        />
      ) : null}
    </Stack>
  );
}
